import {soundMachine} from "@variocube/app-host/src/SoundMachine";
import {classNames, layout, useStorageItemValue, View} from "@variocube/cube-ui";
import * as React from "react";
import {Component, createRef, useCallback} from "react";
import {style, stylesheet} from "typestyle";
import {getSoundByKey} from "../accessibility";
import {useLocalization} from "../i18n";
import IconActivateVoice from "./img/activate_voice_icon_217061.svg";
import BackgroundImage from "./img/bg-800x480.jpg";
import IconDelivery from "./img/icon-usecase_delivery.svg";
import IconDoorBell from "./img/icon-usecase_doorbell.svg";
import IconMail from "./img/icon-usecase_mail.svg";
import IconPickup from "./img/icon-usecase_pickup.svg";
import {PlanCapability, Plans} from "./model";

type HomeViewProps = {
	onDelivery: () => void;
	onDispatch: () => void;
	onPickUp: () => void;
	onDoorBell: () => void;
	backgroundImageUrl?: string;
	doorbell: boolean;
};

export function HomeView({onDelivery, onDispatch, onPickUp, onDoorBell, backgroundImageUrl, doorbell}: HomeViewProps) {
	const {t, language} = useLocalization();

	const customBackground = backgroundImageUrl && style({backgroundImage: `url("${backgroundImageUrl}")`});
	const speaker = useStorageItemValue<boolean>("speaker");

	const plans = useStorageItemValue<Plans>("plans");
	const canDispatch = plans && hasDispatchCapability(Object.values(plans));

	const backgroundDiv = createRef<HTMLDivElement>();

	const handleBackgroundClick = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
		if (e.target === backgroundDiv.current) {
			soundMachine.play(getSoundByKey("SpeakMainScreenInstructions", language));
		}
	}, [backgroundDiv, t, language]);

	const handleReadInstructions = useCallback(() => {
		soundMachine.play(getSoundByKey("SpeakPickupInstructions", language));
	}, [language]);

	return (
		<View>
			<div
				className={classNames(
					layout.fill,
					layout.horizontal,
					layout.center,
					layout.alignCenter,
					styles.view,
					customBackground,
				)}
				onTouchStart={handleBackgroundClick}
				ref={backgroundDiv}
			>
				<HomeViewItem
					iconClass={styles.deliveryIcon}
					text={t("Deliver")}
					onClick={onDelivery}
				/>
				<HomeViewItem
					itemClass={canDispatch ? styles.pickupItem : undefined}
					iconClass={styles.pickUpIcon}
					text={t("PickUp")}
					onClick={onPickUp}
				/>
				{canDispatch && (
					<HomeViewItem
						iconClass={styles.dispatchIcon}
						text={t("Dispatch")}
						onClick={onDispatch}
					/>
				)}
				{doorbell && (
					<HomeViewItem
						itemClass={canDispatch ? styles.doorbellItem : undefined}
						iconClass={styles.doorBellIcon}
						text={t("Ring")}
						onClick={onDoorBell}
					/>
				)}
			</div>
			{speaker
				&& (
					<div className={styles.speakerContainer} onClick={handleReadInstructions}>
						<div className={styles.activateVoiceIcon}></div>
					</div>
				)}
		</View>
	);
}

function hasDispatchCapability(plans: Plans) {
	for (const plan of Object.values(plans)) {
		const {capabilities} = plan;
		if (
			!capabilities // if no capabilities are set, we assume dispatch is enabled
			|| capabilities.indexOf(PlanCapability.OutboundMail) >= 0
			|| capabilities.indexOf(PlanCapability.Transfer) >= 0
		) {
			return true;
		}
	}
	return false;
}

type HomeViewItemProps = {
	itemClass?: string;
	iconClass: string;
	text: string;
	onClick: () => void;
};

class HomeViewItem extends Component<HomeViewItemProps> {
	render() {
		const {itemClass, iconClass, text, onClick} = this.props;
		return (
			<div className={classNames(styles.item, itemClass)} onClick={onClick}>
				<div className={classNames(styles.itemIcon, iconClass)} />
				<div className={styles.itemText}>{text}</div>
			</div>
		);
	}
}

const styles = stylesheet({
	view: {
		background: `url("${BackgroundImage}") no-repeat center center`,
		backgroundSize: "cover",
	},
	deliveryIcon: {
		backgroundImage: `url(${IconDelivery})`,
	},
	pickUpIcon: {
		backgroundImage: `url(${IconPickup})`,
	},
	dispatchIcon: {
		backgroundImage: `url(${IconMail})`,
	},
	doorBellIcon: {
		backgroundImage: `url(${IconDoorBell})`,
	},
	item: {
		background: "var(--color-primary)",
		color: "#fff",
		boxShadow: "0 0.25em 0.75em -0.25em #000",
		height: "27.5vw",
		width: "27.5vw",
		padding: "2em 3em",
		textAlign: "center",
	},
	pickupItem: {
		zIndex: 1,
		filter: "brightness(105%)",
		transform: "scale(1.05)",
	},
	doorbellItem: {
		zIndex: 1,
		filter: "brightness(105%)",
		transform: "scale(1.05)",
	},
	itemIcon: {
		backgroundPosition: "center center",
		backgroundRepeat: "no-repeat",
		height: "5em",
		width: "7em",
		margin: "1em auto",
	},
	itemText: {
		fontWeight: 600,
		fontSize: "2em",
	},
	speakerContainer: {
		position: "absolute",
		width: "5.5em",
		height: "5.5em",
		right: 0,
		bottom: 0,
		backgroundColor: "#FBE500",
		boxShadow: "0 0.25em 0.75em -0.25em #000",
	},
	activateVoiceIcon: {
		backgroundImage: `url(${IconActivateVoice})`,
		backgroundPosition: "center center",
		backgroundSize: "contain",
		width: "5.5em",
		height: "5.5em",
	},
});
